import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "../../../Components/Buttons";
import { FormImage, FormInput, FormSection, FormSwitch } from "../../../Components/Form";
import { ToastMessage } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { blog_category_image_url } from "../../../Components/ImageUrls";
import { handleError } from "../../../stores/components/handleError";
import {
  useCreateBlogCategoryMutation,
  useUpdateBlogCategoryMutation,
} from "../../../stores/api/blogCategories.api";
import { buildFormData } from "../../../stores/api/helpers/payload.helper";

const EMPTY = {
  name: "",
  slug: "",
  image: null,
  image_alt: "",
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
  status: false,
};

const toSlug = (str) => str.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

const CreateBlogCategory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const details = location?.state?.category;

  const [createBlogCategory, { isLoading: creating }] = useCreateBlogCategoryMutation();
  const [updateBlogCategory, { isLoading: updating }] = useUpdateBlogCategoryMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    ...EMPTY,
    ...(details
      ? {
          name: details.name || "",
          slug: details.slug || "",
          image_alt: details.image_alt || "",
          meta_title: details.meta_title || "",
          meta_description: details.meta_description || "",
          meta_keywords: details.meta_keywords || "",
          status: details.status ?? false,
        }
      : {}),
  });

  const setField = (name, value) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "name" && !details) next.slug = toSlug(value);
      return next;
    });
  };

  const resetForm = () => setFormData(EMPTY);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = buildFormData({
        ...formData,
        status: formData.status ? 1 : 0,
      });

      if (details) {
        await updateBlogCategory({ id: details.id, body }).unwrap();
      } else {
        await createBlogCategory(body).unwrap();
      }

      ToastMessage({ type: "success", title: "Success", message: details ? "Category updated!" : "Category created!" });
      if (!details) resetForm();
    } catch (err) {
      handleError(err, "Something went wrong");
    }
  };

  return (
    <div className="p-4">
      <BreadCrumb title={details ? "Edit Blog Category" : "Create Blog Category"} />

      <form onSubmit={onSubmit} className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="Category Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Name" name="name" value={formData.name} required onChange={(val) => setField("name", val)} placeholder="Enter category name" />
            <FormInput label="Slug" name="slug" value={formData.slug} required onChange={(val) => setField("slug", val)} placeholder="auto-generated-from-name" />
          </div>
        </FormSection>

        <FormSection title="Category Image">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormImage
              label="Image"
              required
              initialPreviewUrl={details?.image ? (blog_category_image_url ?? "") + details.image : null}
              onChange={(file) => setFormData((prev) => ({ ...prev, image: file }))}
            />
            <FormInput label="Image Alt Text" name="image_alt" value={formData.image_alt} required onChange={(val) => setField("image_alt", val)} placeholder="Describe the image" />
          </div>
        </FormSection>

        <FormSection title="SEO / Meta">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Meta Title" name="meta_title" value={formData.meta_title} required onChange={(val) => setField("meta_title", val)} placeholder="SEO title" />
            <FormInput label="Meta Keywords" name="meta_keywords" value={formData.meta_keywords} onChange={(val) => setFormData((prev) => ({ ...prev, meta_keywords: val }))} placeholder="keyword1, keyword2" />
            <FormInput label="Meta Description" name="meta_description" value={formData.meta_description} required onChange={(val) => setField("meta_description", val)} placeholder="SEO description" rows={3} className="md:col-span-2" />
          </div>
        </FormSection>

        <FormSection title="Status">
          <FormSwitch label="Status" checked={!!formData.status} onChange={(val) => setFormData((prev) => ({ ...prev, status: val }))} />
        </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button type="button" disabled={loading} onClick={() => { resetForm(); navigate(ROUTE_PATHS.BLOG_CATEGORY); }} className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200">
            Cancel
          </Button>
          <Button type="submit" loading={loading} disabled={loading} className="!bg-green-500 !text-white hover:!bg-green-600" loadingText={details ? "Updating..." : "Creating..."}>
            {details ? "Update Category" : "Create Category"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateBlogCategory;
